/**
 * The "Generate report" affordance on a completed DD — or, when the report is
 * not backed by the data room, the existing request path in its place.
 *
 * Owner rule (2026-10-23): "You shouldn't have access to the generate DD report
 * function if you didn't have prior access to the data room to make your DD."
 * generate-dd-report enforces it (`dd_report_data_room_pass`, answered as an
 * absent report); this asks the same SQL first so the investor is offered the
 * request instead of a button that can only fail. While the answer loads, or if
 * reading it fails, the button stays: the server is the authority, and a hidden
 * button for a paying investor is the worse error (LOADING-GATE INVARIANT).
 */
import React from 'react';
import { View, Text } from 'react-native';
import { Sparkles } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { RequestSourcesAccessCta } from '@/components/score/RequestSourcesAccessCta';
import { useDDReportDataRoomPass } from '@/lib/dd/use-dd-reports';
import { useThemeColors } from '@/lib/useThemeColors';

interface Props {
  /** dd_reports.id */
  reportId: string;
  /** startup_profiles.id */
  startupId: string;
  startupName: string;
  /** dealflow.id when the deal is in the pipeline, else null. */
  dealId: string | null;
  generating: boolean;
  onGenerate: () => void;
}

export function GenerateNarrativeAction({ reportId, startupId, startupName, dealId, generating, onGenerate }: Props) {
  const colors = useThemeColors();
  const pass = useDDReportDataRoomPass(reportId);

  if (pass.data === false) {
    return <RequestSourcesAccessCta startupId={startupId} dealId={dealId} startupName={startupName} purpose="report" />;
  }

  return (
    <View style={{ alignSelf: 'flex-start', borderRadius: 10, overflow: 'hidden', backgroundColor: generating ? colors.border.medium : colors.primary[600] }}>
      <AnimatedPressable
        onPress={() => !generating && onGenerate()}
        disabled={generating}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 10 }}
      >
        <Sparkles size={15} color="#fff" />
        <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }} numberOfLines={1}>
          {generating ? 'Generating…' : 'Generate report'}
        </Text>
      </AnimatedPressable>
    </View>
  );
}
